import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { api, type TipoPallet, type Unidad } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useEstadoGuardado } from '../lib/persistencia';
import { useSesion } from '../lib/sesion';
import { abrir, CURVA, desplegar, fila } from '../components/movimiento';
import { Cambia,
  Buscador,
  Cargando,
  DetalleOrden,
  EstadoChip,
  filtrar,
  Icono,
  Numero,
  Paginador,
  usePaginado,
  PanelError,
  useConfirmar,
  Vacio,
} from '../components/ui';
import {
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  enlace,
  etiquetaCampo,
  fechaImpresa,
  mono,
  nota,
  subtitulo,
  tarjeta,
  hoyISO,
} from '../theme';

// El campo arranca vacío: ahora es un total de piezas, y un 2 puesto de fábrica
// ahí no es una comodidad sino una cantidad inventada esperando que alguien la
// mande sin mirar.
const VACIO = { cantidad: '', observaciones: '', entrega: '' };

/**
 * ZF pide de dos maneras y son pedidos distintos, no dos formas de escribir el
 * mismo. En **capachos** dice cuántas piezas quiere y nada más: en cuántos
 * capachos entra eso lo sabe el depósito, que es el que conoce cuánto de cada
 * material aguanta un capacho. En **pallets completos** dice cuántos pallets
 * quiere: cuántas piezas salen lo trae cada pallet, y cuáles salen lo decide
 * el depósito, que es el único que sabe qué tiene guardado y desde cuándo.
 *
 * Por eso se elige primero: cada manera se sirve de una clase de pallet y de
 * la otra no, así que de eso depende qué artículos hay para pedir.
 */
const COMO_SE_PIDE: Record<Unidad, { rotulo: string; cantidad: string; nota: string; tipo: TipoPallet }> = {
  capacho: {
    rotulo: 'Capachos armados',
    cantidad: 'Piezas totales',
    nota: 'Pedí cuántas piezas necesitás. El depósito abre un pallet y las reparte en capachos.',
    tipo: 'fraccionamiento',
  },
  pallet: {
    rotulo: 'Pallets completos',
    cantidad: 'Pallets',
    nota: 'Sale el pallet entero, con lo que tenga arriba. El depósito elige cuáles: salen primero los que entraron antes.',
    tipo: 'completo',
  },
};

/**
 * En cuántos capachos se va a partir un total. Es la misma cuenta que hace el
 * servidor al recibir el pedido (`repartir`, en `domain/capachos.ts`), repetida
 * acá sólo para mostrarla antes de mandar: quien pide ve el corte, no lo
 * decide. Si los dos no dieran lo mismo manda el servidor, que es el que
 * escribe la orden.
 */
export function corte(total: number, maximo: number | null): { capachos: number; piezas: number; ultimo: number } {
  if (total < 1) return { capachos: 0, piezas: 0, ultimo: 0 };
  const tope = maximo && maximo > 0 ? Math.min(maximo, total) : total;
  const capachos = Math.ceil(total / tope);
  return { capachos, piezas: tope, ultimo: total - tope * (capachos - 1) };
}

/**
 * Un renglón del pedido. Se piden varios de una porque un egreso rara vez es
 * de un solo artículo, y mandar una solicitud por cada uno es el mismo pedido
 * escrito cinco veces.
 *
 * Todos los renglones salen en **una sola** orden de trabajo, con un solo
 * número de seguimiento: es un pedido, no cinco.
 */
type Renglon = { articulo: string; cantidad: string; id?: string };

/**
 * `id` es sólo de pantalla (no viaja a la API): da una `key` estable para que
 * al quitar un renglón del medio salga ése y los demás suban. `crypto.randomUUID`
 * no existe en páginas sin https, por eso no se usa.
 */
const nuevoId = () => Math.random().toString(36).slice(2, 10);
const renglonVacio = (): Renglon => ({ id: nuevoId(), articulo: '', cantidad: VACIO.cantidad });

/** «3 capachos: 45, 45 y 10 piezas» — el corte dicho como lo lee alguien. */
export const cortePlural = (r: { capachos: number; piezas: number; ultimo: number }): string =>
  r.capachos === 1
    ? `1 capacho de ${r.ultimo} piezas`
    : r.ultimo === r.piezas
      ? `${r.capachos} capachos de ${r.piezas} piezas`
      : `${r.capachos} capachos: ${r.capachos - 1} de ${r.piezas} piezas y uno de ${r.ultimo}`;

export default function Solicitud({ onVerStock, onVerOrden }: { onVerStock: () => void; onVerOrden: (id: string) => void }) {
  // Sólo artículos con stock disponible. Nunca una lista de ejemplo: mostrar
  // un artículo que no existe y que desaparece al llegar los datos es peor que
  // no mostrar nada.
  const catalogo = useApi(() => api.articulos(), []);
  const articulos = catalogo.datos ?? [];

  // El pedido a medio armar sobrevive a un F5: perder diez renglones cargados
  // por recargar la página es volver a tipearlos. Al enviarlo se vacía.
  const [unidad, setUnidad] = useEstadoGuardado<Unidad>('solicitud.unidad', 'capacho', (v) => v === 'capacho' || v === 'pallet');
  const [renglones, setRenglones] = useEstadoGuardado<Renglon[]>(
    'solicitud.renglones',
    [renglonVacio()],
    (v) => Array.isArray(v) && v.length > 0 && v.every((r) => typeof r?.articulo === 'string' && typeof r?.cantidad === 'string'),
  );
  const [observaciones, setObservaciones] = useEstadoGuardado('solicitud.observaciones', VACIO.observaciones);
  const [entrega, setEntrega] = useEstadoGuardado('solicitud.entrega', VACIO.entrega);
  const [ticket, setTicket] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [confirmar, dialogo] = useConfirmar();
  const misOrdenesRef = useRef<HTMLDivElement>(null);

  // Lo que quedó guardado de antes de que existieran los `id` los recibe acá.
  useEffect(() => {
    if (renglones.some((r) => !r.id)) setRenglones((antes) => antes.map((r) => (r.id ? r : { ...r, id: nuevoId() })));
  }, [renglones, setRenglones]);

  const como = COMO_SE_PIDE[unidad];
  const porPallet = unidad === 'pallet';
  // Lo que se puede pedir de esta manera. Un pallet de fraccionamiento no sale
  // entero y uno completo no se abre, así que ofrecerlos sería prometer un
  // despacho que el depósito después va a rechazar.
  const disponibles = articulos.filter((a) => a.tipo === como.tipo);

  // Sin default: si nadie eligió, no hay artículo. Mandar el primero de la
  // lista porque sí es pedir algo que nadie pidió.
  const codigoDe = (r: Renglon) => r.articulo;
  // `cantidad` es el total de piezas en capachos y la cantidad de pallets en
  // pallets completos: son dos pedidos distintos con un solo campo, porque en
  // los dos es lo único que escribe quien pide.
  const lineas = renglones.map((r) => {
    const articulo = disponibles.find((a) => a.codigo === codigoDe(r));
    const cantidad = Math.trunc(Number(r.cantidad)) || 0;
    return {
      id: r.id ?? String(renglones.indexOf(r)),
      codigo: codigoDe(r),
      articulo,
      cantidad,
      // Cómo queda partido. En pallets completos no se parte nada.
      corte: porPallet ? null : corte(cantidad, articulo?.maxPorCapacho ?? null),
    };
  });

  const completo = lineas.every((l) => l.codigo && l.cantidad >= 1);
  // Qué falta para poder mandar, dicho en criollo: el renglón y qué le falta.
  const faltante = completo
    ? null
    : (() => {
        const i = lineas.findIndex((l) => !l.codigo || l.cantidad < 1);
        return !lineas[i]!.codigo
          ? `Elegí el artículo del renglón ${i + 1}.`
          : `Falta la cantidad del renglón ${i + 1}.`;
      })();
  // En pallets completos el total es de pallets: no llevan conteo de piezas.
  const total = lineas.reduce((suma, l) => suma + l.cantidad, 0);
  const sinStock = !catalogo.cargando && articulos.length === 0;

  async function cambiarUnidad(nueva: Unidad) {
    // Los artículos son otros: lo elegido para capachos no existe como pallet
    // completo, y dejarlo puesto mandaría un pedido que nadie puede servir.
    // Pero si ya hay algo cargado, borrarlo sin avisar es perder trabajo.
    const cargado = renglones.some((r) => r.articulo || r.cantidad);
    if (
      cargado &&
      !(await confirmar({
        titulo: 'Cambiar cómo lo pedís',
        detalle: 'Los renglones cargados son de la otra manera de pedir y se van a borrar.',
        boton: 'Cambiar y borrar los renglones cargados',
      }))
    )
      return;
    setUnidad(nueva);
    setRenglones([renglonVacio()]);
  }

  const editar = (i: number, cambio: Partial<Renglon>) =>
    setRenglones((antes) => antes.map((r, n) => (n === i ? { ...r, ...cambio } : r)));

  // Al confirmar el envío, bajar la vista hasta «Tus órdenes»: ahí es donde
  // queda el seguimiento, no en una pantalla de tracking que ya no existe.
  useEffect(() => {
    if (ticket) misOrdenesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [ticket]);

  function limpiar() {
    setTicket(null);
    setError('');
    setRenglones([renglonVacio()]);
    setObservaciones(VACIO.observaciones);
    setEntrega(VACIO.entrega);
  }

  async function enviar() {
    setEnviando(true);
    setError('');
    try {
      // Un solo pedido con todos los renglones: entra entero o no entra, así
      // que un error no deja la mitad pedida.
      const res = await api.crearOrden({
        // En capachos va el total y el servidor lo parte; en pallets
        // completos va cuántos pallets.
        lineas: lineas.map((l) =>
          porPallet ? { articulo: l.codigo, capachos: l.cantidad } : { articulo: l.codigo, piezas: l.cantidad },
        ),
        unidad,
        observaciones,
        entregaEstimada: entrega,
      });
      setTicket(res.id);
      setRenglones([renglonVacio()]);
      setObservaciones(VACIO.observaciones);
      setEntrega(VACIO.entrega);
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setEnviando(false);
    }
  }

  const resumen = (id: string, etiqueta: string, valor: React.ReactNode, ultimo = false) => (
    <motion.div
      key={id}
      layout="position"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -16, transition: { duration: 0.2 } }}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 13.5,
        color: c.texto2,
        paddingBottom: ultimo ? 0 : 10,
        borderBottom: ultimo ? 'none' : `1px solid ${c.borde}`,
      }}
    >
      <span>{etiqueta}</span>
      <span style={{ color: c.texto, fontWeight: 500, textAlign: 'right' }}>{valor}</span>
    </motion.div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1000 }}>
      <div>
        <div style={{ ...subtitulo, marginTop: 0 }}>
          Egreso de mercadería, en capachos armados o en pallets completos. Se pueden pedir varios artículos de una:
          salen todos en la misma orden, con un solo número de seguimiento.
        </div>
      </div>

      {catalogo.error && <PanelError mensaje={catalogo.error} onReintentar={catalogo.recargar} />}

      {catalogo.cargando && !catalogo.datos && <Cargando texto="Buscando artículos con stock…" />}

      {sinStock && (
        <div style={tarjeta}>
          <Vacio
            icono="inventory_2"
            titulo="No hay stock para despachar"
            detalle="Una orden de trabajo se arma sobre mercadería que ya está en el depósito. Cargá el inventario o esperá a que se confirme un ingreso."
            accion={
              <button type="button" onClick={onVerStock} style={botonSecundario}>
                Ver el stock
              </button>
            }
          />
        </div>
      )}

      {articulos.length > 0 && (
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 16, alignItems: 'start' }}
        >
          <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Primero cómo lo quiere, porque de eso depende qué se le puede
                ofrecer: son dos stocks distintos, no dos formas de sacar el
                mismo. */}
            <label style={etiquetaCampo}>
              Cómo lo querés
              <select
                value={unidad}
                onChange={(e) => cambiarUnidad(e.target.value as Unidad)}
                style={{ ...campo, padding: '0 12px' }}
              >
                {(['capacho', 'pallet'] as const).map((u) => (
                  <option key={u} value={u}>
                    {COMO_SE_PIDE[u].rotulo}
                  </option>
                ))}
              </select>
              <motion.span key={unidad} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} style={{ fontSize: 12, color: c.texto3, fontWeight: 400 }}>
                {como.nota}
              </motion.span>
            </label>

            {disponibles.length === 0 ? (
              <div style={aviso('warn')}>
                {porPallet
                  ? 'No hay pallets completos disponibles. Los que están guardados son de los que se fraccionan: pedilos como capachos.'
                  : 'No hay pallets para fraccionar. Lo que está guardado sale entero: pedilo como pallets completos.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, position: 'relative' }}>
                <AnimatePresence initial={false} mode="popLayout">
                {renglones.map((r, i) => {
                  const linea = lineas[i]!;
                  return (
                    <motion.div
                      key={linea.id}
                      // Nace pegado al botón «Agregar», que está justo abajo, y los
                      // de abajo se corren (`layout`); al quitar uno, los demás suben.
                      layout="position"
                      initial={{ opacity: 0, scale: 0.88, y: 28, originX: 0.1, originY: 1 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.94, x: -24, transition: { duration: 0.22, ease: CURVA } }}
                      transition={{ duration: 0.38, ease: CURVA }}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        // Siempre recuadrado, también con un solo renglón:
                        // puesto recién a partir del segundo, agregar uno le
                        // cambia el aspecto al primero y la fila salta.
                        padding: 12,
                        border: `1px solid ${c.borde}`,
                        borderRadius: 16,
                      }}
                    >
                      <label style={etiquetaCampo}>
                        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                          Artículo a despachar
                          {renglones.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setRenglones((antes) => antes.filter((_, n) => n !== i))}
                              title="Quitar este renglón"
                              style={{
                                border: 'none',
                                background: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                display: 'grid',
                                placeItems: 'center',
                                // 44 px de área tocable sin correr el resto de
                                // la fila: el margen negativo se come lo que
                                // el tamaño agranda.
                                width: 44,
                                height: 44,
                                margin: '-13px -14px',
                              }}
                            >
                              <Icono nombre="close" size={17} color={c.texto3} />
                            </button>
                          )}
                        </span>
                        <select
                          value={linea.codigo}
                          onChange={(e) => editar(i, { articulo: e.target.value })}
                          style={{ ...campo, padding: '0 12px' }}
                        >
                          <option value="">Elegí un artículo…</option>
                          {disponibles.map((a) => (
                            <option key={a.codigo} value={a.codigo}>
                              {a.etiqueta}
                            </option>
                          ))}
                        </select>
                        {linea.articulo && (
                          <span style={{ fontSize: 12, color: c.texto3, fontWeight: 400 }}>
                            {porPallet
                              ? `${linea.articulo.pallets} ${linea.articulo.pallets === 1 ? 'pallet entero disponible' : 'pallets enteros disponibles'}`
                              : `${linea.articulo.piezas.toLocaleString('es-AR')} piezas disponibles en ${linea.articulo.pallets} ${linea.articulo.pallets === 1 ? 'pallet' : 'pallets'}`}
                          </span>
                        )}
                        {/* El backend acepta un pedido más grande de lo que hay:
                            no lo bloqueamos, pero avisamos antes de que lo mande
                            sin saberlo. */}
                        <AnimatePresence initial={false}>
                          {linea.articulo &&
                            linea.cantidad > (porPallet ? linea.articulo.pallets : linea.articulo.piezas) && (
                              <motion.div {...desplegar}>
                                <div style={{ ...aviso('warn'), padding: '9px 12px', fontSize: 12.5 }}>
                                  Pediste {linea.cantidad}, hay {porPallet ? linea.articulo.pallets : linea.articulo.piezas}
                                  . El depósito te va a avisar si falta.
                                </div>
                              </motion.div>
                            )}
                        </AnimatePresence>
                      </label>

                      {/* Un solo número: el total de piezas, o cuántos pallets.
                          Cómo se parte en capachos ya no se escribe acá —es
                          trabajo del depósito— y se muestra abajo, resuelto. */}
                      <label style={etiquetaCampo}>
                        {como.cantidad}
                        <input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          value={r.cantidad}
                          onChange={(e) => editar(i, { cantidad: e.target.value })}
                          style={campo}
                        />
                      </label>

                      <AnimatePresence initial={false}>
                      {linea.corte && linea.corte.capachos > 0 && (
                        <motion.div
                          {...desplegar}
                          style={{ ...desplegar.style }}
                        >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '9px 12px',
                            borderRadius: 12,
                            background: c.fondo,
                            border: `1px solid ${c.borde}`,
                            fontSize: 12.5,
                            color: c.texto2,
                          }}
                        >
                          <Icono nombre="inventory_2" size={16} color={c.texto3} />
                          <span>
                            {linea.articulo?.maxPorCapacho
                              ? `Salen ${cortePlural(linea.corte)}.`
                              : 'El depósito define en cuántos capachos se reparte.'}
                          </span>
                        </div>
                        </motion.div>
                      )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
                </AnimatePresence>

                <motion.button
                  layout="position"
                  type="button"
                  onClick={() => setRenglones((antes) => [...antes, renglonVacio()])}
                  style={{ ...botonSecundario, alignSelf: 'flex-start', padding: '9px 14px' }}
                >
                  <Icono nombre="add" size={18} color={c.texto3} />
                  Agregar otro artículo
                </motion.button>
              </div>
            )}

            <label style={etiquetaCampo}>
              Fecha de entrega estimada
              <input
                type="date"
                value={entrega}
                min={hoyISO()}
                onChange={(e) => setEntrega(e.target.value)}
                style={{ ...campo, alignSelf: 'flex-start' }}
              />
              <span style={{ fontSize: 12, color: c.texto3, fontWeight: 400 }}>
                Opcional. Para cuándo lo necesitás; vale para todos los renglones del pedido.
              </span>
            </label>

            <label style={etiquetaCampo}>
              Observaciones para el operario
              <textarea
                rows={3}
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Ej. film y esquineros en los dos capachos"
                style={{
                  padding: '12px 14px',
                  border: `1px solid ${c.bordeFuerte}`,
                  borderRadius: 16,
                  background: c.panel,
                  fontSize: 13.5,
                  color: c.texto,
                  resize: 'vertical',
                }}
              />
              <span style={{ fontSize: 12, color: c.texto3, fontWeight: 400 }}>
                Se muestra en la pantalla de armado del depósito, en todos los renglones del pedido.
              </span>
            </label>

            {/* Éxito y error van junto al botón, no arriba de todo: en el
                celular arriba de todo queda fuera de la pantalla. */}
            <AnimatePresence>
            {ticket && (
              <motion.div {...abrir(16)} role="status" style={{ ...aviso('ok'), display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <Icono nombre="task_alt" size={24} color={c.verde} />
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Orden enviada al depósito</div>
                  <div style={{ marginTop: 2, fontSize: 13, color: c.texto2 }}>
                    Comprobante <span style={{ fontFamily: mono, fontWeight: 500, color: c.texto }}>{ticket}</span> ·
                    seguila abajo, en Tus órdenes.
                  </div>
                </div>
                <button type="button" onClick={limpiar} style={{ ...botonSecundario, padding: '9px 16px' }}>
                  Otra orden
                </button>
              </motion.div>
            )}
            </AnimatePresence>

            {error && <PanelError mensaje={error} />}

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={enviar}
                disabled={enviando || !completo || disponibles.length === 0}
                style={{ ...botonPrimario, opacity: enviando || !completo || disponibles.length === 0 ? 0.6 : 1 }}
              >
                <Cambia>{enviando ? 'Enviando…' : 'Enviar solicitud'}</Cambia>
              </button>
              <button type="button" onClick={limpiar} style={botonSecundario}>
                Limpiar
              </button>
              <AnimatePresence mode="wait" initial={false}>
                {faltante && disponibles.length > 0 && (
                  <motion.span
                    key={faltante}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18, ease: CURVA }}
                    style={{ fontSize: 12.5, color: c.texto3 }}
                  >
                    {faltante}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, position: 'relative' }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>Resumen del pedido</span>
            <AnimatePresence initial={false} mode="popLayout">
              {lineas.map((l, i) =>
                resumen(
                  l.id,
                  l.articulo?.etiqueta ?? '—',
                  porPallet ? (
                    <>
                      <Numero valor={l.cantidad} formato={String} /> {l.cantidad === 1 ? 'pallet' : 'pallets'}
                    </>
                  ) : (
                    <>
                      <Numero valor={l.cantidad} formato={String} /> pz
                      {l.articulo?.maxPorCapacho && l.corte ? ` · ${l.corte.capachos} cap.` : ''}
                    </>
                  ),
                  i === lineas.length - 1,
                ),
              )}
            </AnimatePresence>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13.5, color: c.texto2 }}
            >
              <span>{porPallet ? 'Total de pallets' : 'Total de piezas'}</span>
              <span
                style={{
                  fontSize: 27,
                  fontWeight: 600,
                  letterSpacing: '-0.03em',
                  color: c.texto,
                  fontVariantNumeric: 'tabular-nums',
                  textAlign: 'right',
                }}
              >
                <Numero valor={total} formato={String} />
              </span>
            </div>
            <div style={{ ...nota, marginTop: 4 }}>
              El depósito confirma el armado dentro de las 24 h hábiles. Cualquier faltante se informa antes de
              despachar.
            </div>
          </div>
        </div>
      )}

      <div ref={misOrdenesRef}>
        <MisOrdenes onVerOrden={onVerOrden} />
      </div>
      {dialogo}
    </div>
  );
}

/**
 * Las órdenes que pidió quien está mirando, con en qué anda cada una.
 *
 * Va acá y no en el inicio porque el cliente no tiene inicio: ésta es su
 * pantalla, y pedir sin poder ver después qué pasó con lo pedido es la mitad
 * del trámite. El depósito ve lo mismo pero con las de todos, que es su cola de
 * trabajo.
 *
 * Se mantiene sola: el estado lo mueve el depósito del otro lado.
 */
function MisOrdenes({ onVerOrden }: { onVerOrden: (id: string) => void }) {
  // La mesa de la orden es del depósito: ZF, que sólo pide, no entra ahí.
  const puedeVerOrden = useSesion().vistas.includes('frac');
  const { datos, cargando, error, recargar } = useApi(() => api.ordenes(), [], VIVO.normal);
  const [busqueda, setBusqueda] = useState('');
  const [confirmar, dialogo] = useConfirmar();
  const [cancelando, setCancelando] = useState<string | null>(null);
  const [errorCancelar, setErrorCancelar] = useState('');

  const encontradas = filtrar(datos ?? [], busqueda, (o) => [
    o.id,
    o.detalle,
    o.estado,
    ...o.articulos,
    ...o.lineas.map((l) => l.detalle),
    o.observaciones,
  ]);
  const pagina = usePaginado(encontradas, 8);

  // Sólo se puede cancelar en el primer estado: una vez que el depósito tocó
  // algo, cancelar de este lado la dejaría comprometida sin que nadie avise.
  async function cancelar(id: string) {
    if (
      !(await confirmar({
        titulo: `¿Cancelar la ${id}?`,
        detalle: 'El depósito recibe el aviso para no prepararla.',
        boton: 'Cancelar la orden',
      }))
    )
      return;
    setErrorCancelar('');
    setCancelando(id);
    try {
      await api.cancelarOrden(id);
      recargar();
    } catch (err) {
      setErrorCancelar(mensajeDeError(err));
    } finally {
      setCancelando(null);
    }
  }

  return (
    <div style={{ ...tarjeta, overflow: 'hidden', position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 16px',
          borderBottom: `1px solid ${c.borde}`,
          flexWrap: 'wrap',
        }}
      >
        <Icono nombre="fact_check" size={19} color={c.texto3} />
        <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>Tus órdenes</span>
        <span style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {(datos?.length ?? 0) > 0 && (
            <Buscador valor={busqueda} onCambiar={setBusqueda} placeholder="Comprobante, artículo o estado" ancho={300} />
          )}
        </span>
      </div>

      {errorCancelar && <PanelError mensaje={errorCancelar} />}

      {error ? (
        <PanelError mensaje={error} onReintentar={recargar} />
      ) : cargando && !datos ? (
        <Cargando texto="Buscando tus órdenes…" />
      ) : encontradas.length === 0 ? (
        <Vacio
          icono="assignment_add"
          titulo={datos?.length ? 'Sin resultados' : 'Todavía no pediste ninguna'}
          detalle={
            datos?.length
              ? 'Ninguna orden coincide con lo que buscaste.'
              : 'Las órdenes que pidas aparecen acá con su número de comprobante y en qué anda cada una.'
          }
        />
      ) : (
        <AnimatePresence mode="popLayout">
        {pagina.visibles.map((o, i) => (
          <motion.div
            key={o.id}
            layout="position"
            {...fila(i)}
            // En el celular: comprobante y estado arriba, el detalle a lo ancho
            // abajo y «entregados» con «Cancelar» en el último renglón. Desde
            // tablet, la fila de siempre.
            className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5 tablet:flex tablet:flex-wrap tablet:gap-3"
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${c.borde}`,
            }}
          >
            <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 600, color: c.texto, minWidth: 82 }}>
              {puedeVerOrden ? (
                <button type="button" onClick={() => onVerOrden(o.id)} title="Ver la orden" style={{ ...enlace, fontSize: 13, fontWeight: 600 }}>
                  {o.id}
                </button>
              ) : (
                o.id
              )}
            </span>
            <span className="col-span-2 row-start-2" style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <DetalleOrden orden={o} />
              {o.entregaEstimada && (
                <span style={{ fontSize: 12.5, color: c.texto3 }}>Entrega estimada: {fechaImpresa(o.entregaEstimada)}</span>
              )}
              {o.observaciones && (
                <span style={{ fontSize: 12.5, color: c.texto3, textWrap: 'pretty' }}>{o.observaciones}</span>
              )}
              {o.faltante && (
                <span style={{ fontSize: 12.5, color: c.ambar, textWrap: 'pretty' }}>Se cerró con faltante: {o.faltante}.</span>
              )}
            </span>
            {/* Cuánto de lo pedido ya salió: es lo que se quiere saber cuando
                se mira el seguimiento, más que el nombre del estado. */}
            <span className="col-start-1 row-start-3" style={{ fontSize: 12.5, color: c.texto3, fontVariantNumeric: 'tabular-nums' }}>
              <Numero valor={o.entregadas} formato={String} /> de <Numero valor={o.salidas} formato={String} />{' '}
              {o.unidad === 'pallet' ? 'pallets' : 'capachos'} entregados
            </span>
            <span className="col-start-2 row-start-1 justify-self-end">
              <EstadoChip estado={o.estado} />
            </span>
            {/* Sólo mientras nadie la tocó: una vez aceptada o armada, el
                depósito es quien la frena, no ZF por acá. */}
            <AnimatePresence initial={false}>
              {o.estado === 'Pendiente' && (
                <motion.button
                  key="cancelar"
                  className="col-start-2 row-start-3 justify-self-end"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  transition={{ duration: 0.2, ease: CURVA }}
                  type="button"
                  onClick={() => cancelar(o.id)}
                  disabled={cancelando === o.id}
                  style={{ ...botonSecundario, padding: '7px 12px', fontSize: 12.5, opacity: cancelando === o.id ? 0.6 : 1 }}
                >
                  <Cambia>{cancelando === o.id ? 'Cancelando…' : 'Cancelar'}</Cambia>
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
        </AnimatePresence>
      )}

      <Paginador
        pagina={pagina.pagina}
        paginas={pagina.paginas}
        total={pagina.total}
        desde={pagina.desde}
        porPagina={8}
        que="órdenes"
        onIr={pagina.setPagina}
      />
      {dialogo}
    </div>
  );
}
